// The `log` interface: what a plugin writes when something is worth knowing,
// and nothing about where it goes. A provider (log-logtape today) decides the
// sinks, the levels that pass, and any extra handling such as shipping records
// to a backend. Plugins never call `console` directly.

export const LOG_API = 'log';
export const LOG_VERSION = '1.0.0';

export type LogLevel = 'debug' | 'info' | 'warn' | 'error';

/** Structured context attached to a record. Prefer fields over interpolation. */
export type Fields = Record<string, unknown>;

export interface Log {
  /**
   * A logger for a narrower category under this one. A plugin takes
   * `log.child(app.meta.name)` once in `setup`, and a feature inside it may
   * narrow further, e.g. `child('import')`.
   */
  child(category: string): Log;
  debug(message: string, fields?: Fields): void;
  info(message: string, fields?: Fields): void;
  warn(message: string, fields?: Fields): void;
  /** `fields` may be the Error itself; the provider records its message and stack. */
  error(message: string, fields?: Fields | Error): void;
}

/** One record as a provider sees it; what a sink receives. */
export interface LogRecord {
  level: LogLevel;
  /** From the root outward, e.g. ['chess', 'library-import-chesscom', 'import']. */
  category: string[];
  message: string;
  fields: Fields;
  timestamp: number;
}

/** Normalises the `error()` argument into fields a sink can serialise. */
export function errorFields(fields?: Fields | Error): Fields {
  if (fields instanceof Error) {
    return { error: { name: fields.name, message: fields.message, stack: fields.stack } };
  }
  return fields ?? {};
}

export const LEVEL_ORDER: Record<LogLevel, number> = { debug: 0, info: 1, warn: 2, error: 3 };

/** Whether a record at `level` passes a `lowest` threshold. */
export function passes(level: LogLevel, lowest: LogLevel): boolean {
  return LEVEL_ORDER[level] >= LEVEL_ORDER[lowest];
}
